/**
 * Cliente del API de usuarios administradores del portal (tabla `admin_users`).
 * Gestión contra la base de datos vía REST.
 */
import { tokenStore } from '@/lib/auth/tokenStore'
import { getPortalApiBase } from '@/lib/env'

const ENDPOINT = `${getPortalApiBase()}/admin-users`

export type AdminRole = 'ADMIN' | 'EDITOR'

export interface AdminUser {
  id: string
  email: string
  name: string
  role: AdminRole
  active: boolean
  createdAt?: string
  updatedAt?: string
}

export type AdminUserInput = Pick<AdminUser, 'email' | 'name' | 'role' | 'active'>

/**
 * Cabeceras con el Bearer del admin (mismo patrón que commercials y adminApi de
 * pagos). La gestión de administradores exige un admin ADMIN
 * verificado en portal-server (goal seguridad-remediacion 12).
 */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const token = tokenStore.get()
  return {
    Accept: 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  }
}

async function json<T>(res: Response, ctx: string): Promise<T> {
  if (!res.ok) {
    let message = `${ctx}: ${res.status}`
    try {
      const body = await res.json()
      if (body?.message) message = Array.isArray(body.message) ? body.message.join(', ') : body.message
    } catch {
      /* sin cuerpo */
    }
    throw new Error(message)
  }
  const text = await res.text()
  return (text.trim() ? JSON.parse(text) : null) as T
}

export async function fetchAdminUsers(): Promise<AdminUser[]> {
  const res = await fetch(ENDPOINT, { headers: authHeaders() })
  return (await json<AdminUser[] | null>(res, 'GET admin-users')) ?? []
}

export async function createAdminUser(input: AdminUserInput): Promise<AdminUser> {
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  })
  return json<AdminUser>(res, 'POST admin-users')
}

export async function updateAdminUser(id: string, input: AdminUserInput): Promise<AdminUser> {
  const res = await fetch(`${ENDPOINT}/${id}`, {
    method: 'PUT',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(input),
  })
  return json<AdminUser>(res, 'PUT admin-users/:id')
}

export async function deleteAdminUser(id: string): Promise<void> {
  const res = await fetch(`${ENDPOINT}/${id}`, { method: 'DELETE', headers: authHeaders() })
  if (!res.ok) {
    let message = `DELETE admin-users/:id: ${res.status}`
    try {
      const body = await res.json()
      if (body?.message) message = body.message
    } catch {
      /* sin cuerpo */
    }
    throw new Error(message)
  }
}

export const ROLE_LABELS: Record<AdminRole, string> = {
  ADMIN: 'Administrador',
  EDITOR: 'Editor (Lo último)',
}

export type AdminProfile = Pick<AdminUser, 'email' | 'name' | 'role' | 'active'>

/** Devuelve el perfil de admin del usuario logado, o null si no es admin del portal. */
export async function fetchMyAdminProfile(): Promise<AdminProfile | null> {
  const token = tokenStore.get()
  if (!token) return null
  const res = await fetch(`${ENDPOINT}/me`, {
    headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
  })
  if (!res.ok) return null
  return res.json() as Promise<AdminProfile>
}
